/* eslint-disable no-nested-ternary */
import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import DsDropdown from '../Dropdown';
import { DownloadIcon, FilterIcon } from '../Icons';
import { FlexRow, FlexColumn, Row } from '../GridsAndLayouts/GridsAndLayouts';
import Typography from '../Typography';
import './Table.scss';

function renderCardTitle(title) {
  if (title == null || title === '') return null;
  if (typeof title === 'string' || typeof title === 'number') {
    return (
      <FlexRow align="center" className="ds-table-card__title" fullWidth>
        <Typography variant="subtitle1">{title}</Typography>
      </FlexRow>
    );
  }
  return (
    <FlexRow align="center" className="ds-table-card__title" fullWidth>
      {title}
    </FlexRow>
  );
}

function renderHeadLabel(label) {
  if (label == null) return null;
  if (typeof label === 'string' || typeof label === 'number') {
    return (
      <FlexRow inline align="center" className="ds-table__head-label" fullWidth={false}>
        <Typography variant="t16">{label}</Typography>
      </FlexRow>
    );
  }
  return (
    <FlexRow inline align="center" className="ds-table__head-label" fullWidth={false}>
      {label}
    </FlexRow>
  );
}

function renderStackLine(content, className, variant) {
  if (content == null || content === '') return null;
  if (typeof content === 'string' || typeof content === 'number') {
    return (
      <Typography variant={variant} className={className}>
        {content}
      </Typography>
    );
  }
  return <FlexColumn className={className}>{content}</FlexColumn>;
}

function renderHeaderAction(action, index = 0) {
  const reactKey =
    action.id != null ? String(action.id) : action.key != null ? String(action.key) : `header-action-${index}`;

  if (action.type === 'dropdown') {
    return (
      <DsDropdown.ActionMenu
        key={reactKey}
        className={action.className ?? 'ds-tile-toolbar__action-menu'}
        options={action.options}
        onChange={action.onChange}
        triggerIcon={action.triggerIcon}
        triggerAriaLabel={action.ariaLabel}
        align={action.align ?? 'right'}
        disabled={Boolean(action.disabled)}
      />
    );
  }

  if (action.type === 'button') {
    return (
      <Button
        key={reactKey}
        type="button"
        variant="tertiary"
        size="sm"
        className="ds-table-card__icon-btn"
        aria-label={action.ariaLabel}
        disabled={Boolean(action.disabled)}
        onClick={action.onClick}
        icon={action.icon}
      />
    );
  }

  return null;
}

function renderTableMessage(messageId, className, message) {
  return (
    <Typography variant="bodyMedium" color="muted" component="p" id={messageId} className={className}>
      {message}
    </Typography>
  );
}

function renderTableBody({
  columns,
  data,
  rowKey,
  rowDomIdPrefix,
  rowClassName,
  rowAttributes,
  tableClasses,
  showTableHead,
  renderCell,
  loading,
  loadingMessage,
  noDataMessage,
  messageIdPrefix,
}) {
  return (
    <table className={tableClasses}>
      {showTableHead ? (
        <thead className="ds-table__head">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className="ds-table__head-cell"
                style={column.width ? { width: column.width } : undefined}
              >
                {renderHeadLabel(column.label)}
              </th>
            ))}
          </tr>
        </thead>
      ) : null}
      <tbody className="ds-table__body">
        {loading ? (
          <tr>
            <td colSpan={columns.length} className="ds-table__cell">
              {renderTableMessage(`${messageIdPrefix}-loading`, 'ds-table__loading-message', loadingMessage)}
            </td>
          </tr>
        ) : data.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="ds-table__cell">
              {renderTableMessage(`${messageIdPrefix}-no-data`, 'ds-table__no-data-message', noDataMessage)}
            </td>
          </tr>
        ) : (
          data.map((row, index) => {
            const rowClass = ['ds-table__row', rowClassName ? rowClassName(row, index) : ''].filter(Boolean).join(' ');
            const rowExtraAttrs = rowAttributes ? rowAttributes(row, index) : {};
            return (
              <tr
                key={rowKey(row, index)}
                id={rowDomIdPrefix ? `${rowDomIdPrefix}-row-${index}` : undefined}
                className={rowClass}
                {...rowExtraAttrs}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    id={rowDomIdPrefix ? `${rowDomIdPrefix}-row-${index}-cell-${column.key}` : undefined}
                    className="ds-table__cell"
                    style={column.width ? { width: column.width } : undefined}
                  >
                    {renderCell ? renderCell({ row, column, index }) : row[column.key] ?? ''}
                  </td>
                ))}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

const Table = ({
  columns,
  data,
  rowKey = (_row, index) => index,
  rowDomIdPrefix,
  rowClassName,
  rowAttributes,
  className = '',
  tableClassName = '',
  renderCell,
  variant = 'without-avatar',
  layout = 'card',
  density = 'default',
  tableLayout = 'fixed',
  showVerticalDividers = false,
  showCardHeader = false,
  showTitle = true,
  title,
  showFilterButton = false,
  showDownloadButton = false,
  filterButtonLabel = 'Filter',
  downloadButtonLabel = 'Download',
  onFilterClick,
  onDownloadClick,
  headerActions,
  showTableHead = true,
  rowHover = true,
  striped = false,
  showPagination = false,
  pagination,
  loading = false,
  noDataMessage = 'No data found',
  loadingMessage = 'Loading...',
}) => {
  const messageIdPrefix = useId();
  const showTitleBlock = showTitle && title != null && title !== '';
  const hasHeaderActions = Array.isArray(headerActions) && headerActions.length > 0;
  const showLegacyActions = !hasHeaderActions && (showFilterButton || showDownloadButton);
  const showActions = hasHeaderActions || showLegacyActions;
  const renderToolbar = showCardHeader && (showTitleBlock || showActions);

  const tableClasses = [
    'ds-table',
    `ds-table--${variant}`,
    layout === 'embedded' && 'ds-table--embedded',
    density !== 'default' && `ds-table--density-${density}`,
    tableLayout === 'auto' && 'ds-table--layout-auto',
    showVerticalDividers && 'ds-table--with-vertical-dividers',
    rowHover && 'ds-table--row-hover',
    striped && 'ds-table--striped',
    tableClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const cardClasses = ['ds-table-card', layout === 'embedded' && 'ds-table-card--embedded', className]
    .filter(Boolean)
    .join(' ');

  return (
    <FlexColumn className={cardClasses}>
      <FlexColumn className={`ds-table-card__stack${renderToolbar ? ' ds-table-card__stack--with-toolbar' : ''}`}>
        {renderToolbar && (
          <FlexRow
            align="center"
            justify={!showTitleBlock && showActions ? 'end' : 'between'}
            fullWidth
            className={`ds-table-card__header${
              !showTitleBlock && showActions ? ' ds-table-card__header--actions-only' : ''
            }`}
          >
            {showTitleBlock ? renderCardTitle(title) : null}
            {showActions ? (
              <Row noGutters className="ds-table-card__actions">
                {hasHeaderActions ? headerActions.map((item, index) => renderHeaderAction(item, index)) : null}
                {showLegacyActions ? (
                  <>
                    {showFilterButton ? (
                      <Button
                        variant="tertiary"
                        size="sm"
                        className="ds-table-card__icon-btn"
                        aria-label={filterButtonLabel}
                        onClick={onFilterClick}
                        icon={<FilterIcon className="ds-table-card__icon" size={18} />}
                      />
                    ) : null}
                    {showDownloadButton ? (
                      <Button
                        variant="tertiary"
                        size="sm"
                        className="ds-table-card__icon-btn"
                        aria-label={downloadButtonLabel}
                        onClick={onDownloadClick}
                        icon={<DownloadIcon className="ds-table-card__icon" size={18} />}
                      />
                    ) : null}
                  </>
                ) : null}
              </Row>
            ) : null}
          </FlexRow>
        )}

        <FlexColumn className="ds-table-card__table-wrap" fullWidth>
          {renderTableBody({
            columns,
            data,
            rowKey,
            rowDomIdPrefix,
            rowClassName,
            rowAttributes,
            tableClasses,
            showTableHead,
            renderCell,
            loading,
            loadingMessage,
            noDataMessage,
            messageIdPrefix,
          })}
        </FlexColumn>

        {showPagination && pagination ? (
          <Row noGutters className="ds-table-card__pagination">
            {pagination}
          </Row>
        ) : null}
      </FlexColumn>
    </FlexColumn>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  rowKey: PropTypes.func,
  rowDomIdPrefix: PropTypes.string,
  rowClassName: PropTypes.func,
  rowAttributes: PropTypes.func,
  className: PropTypes.string,
  tableClassName: PropTypes.string,
  renderCell: PropTypes.func,
  variant: PropTypes.oneOf(['with-avatar', 'without-avatar']),
  layout: PropTypes.oneOf(['card', 'embedded']),
  density: PropTypes.oneOf(['default', 'comfortable', 'compact']),
  tableLayout: PropTypes.oneOf(['fixed', 'auto']),
  showVerticalDividers: PropTypes.bool,
  showCardHeader: PropTypes.bool,
  showTitle: PropTypes.bool,
  title: PropTypes.node,
  showFilterButton: PropTypes.bool,
  showDownloadButton: PropTypes.bool,
  filterButtonLabel: PropTypes.string,
  downloadButtonLabel: PropTypes.string,
  onFilterClick: PropTypes.func,
  onDownloadClick: PropTypes.func,
  headerActions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        type: PropTypes.oneOf(['button']).isRequired,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        icon: PropTypes.node.isRequired,
        ariaLabel: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
      }),
      PropTypes.shape({
        type: PropTypes.oneOf(['dropdown']).isRequired,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        triggerIcon: PropTypes.node.isRequired,
        ariaLabel: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.node.isRequired,
            subtitle: PropTypes.node,
            avatar: PropTypes.node,
            icon: PropTypes.node,
          })
        ).isRequired,
        onChange: PropTypes.func.isRequired,
        align: PropTypes.oneOf(['left', 'right', 'center']),
        disabled: PropTypes.bool,
        className: PropTypes.string,
      }),
    ])
  ),
  showTableHead: PropTypes.bool,
  rowHover: PropTypes.bool,
  striped: PropTypes.bool,
  showPagination: PropTypes.bool,
  pagination: PropTypes.node,
  loading: PropTypes.bool,
  noDataMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
};

Table.defaultProps = {
  rowKey: (_row, index) => index,
  rowDomIdPrefix: undefined,
  rowClassName: undefined,
  rowAttributes: undefined,
  className: '',
  tableClassName: '',
  renderCell: undefined,
  variant: 'without-avatar',
  layout: 'card',
  density: 'default',
  tableLayout: 'fixed',
  showVerticalDividers: false,
  showCardHeader: false,
  showTitle: true,
  title: undefined,
  showFilterButton: false,
  showDownloadButton: false,
  filterButtonLabel: 'Filter',
  downloadButtonLabel: 'Download',
  onFilterClick: undefined,
  onDownloadClick: undefined,
  headerActions: undefined,
  showTableHead: true,
  rowHover: true,
  striped: false,
  showPagination: false,
  pagination: undefined,
  loading: false,
  noDataMessage: 'No data found',
  loadingMessage: 'Loading...',
};

export const TableAvatarText = ({
  avatar = '',
  fallback = '',
  title = '',
  subtitle = '',
  size = 'md',
  shape = 'circle',
  fallbackVariant = 'default',
  fullWidth = true,
}) => {
  const hasAvatarUrl = Boolean(avatar && String(avatar).trim());
  const [showAvatarImage, setShowAvatarImage] = useState(hasAvatarUrl);

  useEffect(() => {
    setShowAvatarImage(hasAvatarUrl);
  }, [avatar, hasAvatarUrl]);

  const showImage = hasAvatarUrl && showAvatarImage;
  let altText = '';
  if (title != null && title !== '') {
    altText = typeof title === 'string' || typeof title === 'number' ? String(title) : 'avatar';
  }

  const avatarClass = [
    'ds-table__avatar',
    size !== 'md' && `ds-table__avatar--${size}`,
    shape === 'square' && 'ds-table__avatar--square',
  ]
    .filter(Boolean)
    .join(' ');
  const fallbackClass = [
    'ds-table__avatar-fallback',
    size !== 'md' && `ds-table__avatar-fallback--${size}`,
    shape === 'square' && 'ds-table__avatar-fallback--square',
    fallbackVariant === 'theme' && 'ds-table__avatar-fallback--theme',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FlexRow
      align={size === 'sm' ? 'center' : 'start'}
      className={['ds-table__avatar-text', size !== 'md' && `ds-table__avatar-text--size-${size}`]
        .filter(Boolean)
        .join(' ')}
      fullWidth={fullWidth}
    >
      {showImage ? (
        <img
          src={avatar}
          alt={altText}
          className={avatarClass}
          onError={() => setShowAvatarImage(false)}
          aria-hidden={altText === '' ? true : undefined}
        />
      ) : (
        <FlexRow align="center" justify="center" inline className={fallbackClass} fullWidth={false}>
          {fallback}
        </FlexRow>
      )}
      {(title != null && title !== '') || (subtitle != null && subtitle !== '') ? (
        <FlexColumn className="ds-table__content">
          {renderStackLine(title, 'ds-table__title', 'buttonMedium')}
          {renderStackLine(subtitle, 'ds-table__subtitle', 'body1')}
        </FlexColumn>
      ) : null}
    </FlexRow>
  );
};

TableAvatarText.propTypes = {
  avatar: PropTypes.string,
  fallback: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  shape: PropTypes.oneOf(['circle', 'square']),
  fallbackVariant: PropTypes.oneOf(['default', 'theme']),
  fullWidth: PropTypes.bool,
};

TableAvatarText.defaultProps = {
  avatar: '',
  fallback: '',
  title: '',
  subtitle: '',
  size: 'md',
  shape: 'circle',
  fallbackVariant: 'default',
  fullWidth: true,
};

export const TableTextStack = ({ title = '', subtitle = '' }) => (
  <FlexColumn className="ds-table__content">
    {renderStackLine(title, 'ds-table__title', 'buttonMedium')}
    {renderStackLine(subtitle, 'ds-table__subtitle', 'body1')}
  </FlexColumn>
);

TableTextStack.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
};

TableTextStack.defaultProps = {
  title: '',
  subtitle: '',
};

export const TableBadge = ({ children = '' }) => (
  <Typography component="span" variant="body2" className="ds-table__badge">
    {children}
  </Typography>
);

TableBadge.propTypes = {
  children: PropTypes.node,
};

TableBadge.defaultProps = {
  children: '',
};

export const TableLink = ({ children = '', onClick }) => (
  <Button variant="link" size="sm" className="ds-table__link" onClick={onClick}>
    {typeof children === 'string' || typeof children === 'number' ? (
      <Typography component="span" variant="body1">
        {children}
      </Typography>
    ) : (
      children
    )}
  </Button>
);

TableLink.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
};

TableLink.defaultProps = {
  children: '',
  onClick: undefined,
};

export default Table;
